'use client';

import { IProduct, type ProductModel } from '@/services/models/Product';
import { VariationAttributeType } from '@/services/models/Product/variation-attributes';

import { msg, msgf } from '@/services/isomorphic/I18NService';
import type { Nullable } from '@/type-utils';
import { observer } from 'mobx-react-lite';
import { isNullish, isNullOrEmpty } from '@/utils/null-utils';
import type { IGiftCardLineItem } from '@/services/models/Cart/LineItem';
import type { FC } from 'react';
import { useLocaleMessage } from '@/react/hooks/useLocaleMessage';
import { maskEmail } from '@/utils/string-utils';
import { MoneyModel } from '@/services/models/Money';
import type { IOrderLine } from '@/services/models/Order';
import { Button } from '../../../core-ui/Button';
import SkeletonLoader from '../../../utility/SkeletonLoader';
import { useProductVM } from '../../../../hooks/useProductVM';

import S from './styles.module.scss';
import { cart_cartItem_color } from "@/lang/__generated__/ahnu/cart_cartItem_color";
import { cart_cartItem_editDetails } from "@/lang/__generated__/ahnu/cart_cartItem_editDetails";
import { giftcard_cart_to } from "@/lang/__generated__/ahnu/giftcard_cart_to";
import { giftcard_cart_from } from "@/lang/__generated__/ahnu/giftcard_cart_from";
import { giftcard_cart_total } from "@/lang/__generated__/ahnu/giftcard_cart_total";
import { cart_cartItem_genderAndSize } from "@/lang/__generated__/ahnu/cart_cartItem_genderAndSize";
import { cart_cartItem_sizeDisplay } from "@/lang/__generated__/ahnu/cart_cartItem_sizeDisplay";

/**
 * Line Item details props definition.
 */
export interface IProductLineItemDetailsProps {
  /** The product line item from cart. */
  product: Nullable<ProductModel>;
  /**
   * The style of the product line item.
   * Simple option is displayed without cart components, used in
   * the AddToCartModal.
   */
  variant?: 'default' | 'preview' | 'simple';
  /**
   * Callback that will be executed when the `Edit Details` link is
   * clicked on this line item. If omitted, the link will not be displayed.
   * @param product - The product corresponding to the line item.
   */
  onClickEdit?: (product: IProduct) => void;
  /**
   * Controls the visibility of the action buttons in the component.
   * If `false`, the `Remove` will not be displayed.
   * @default true
   */
  showActions?: boolean;
}

/** The details section of the line item component. */
export const ProductLineItemDetails = observer(function LineItemDetails({
  product,
  variant,
  onClickEdit,
  showActions = true
}: IProductLineItemDetailsProps) {
  const productVM = useProductVM(product);

  const color = product?.getSelectedAttributeByType(
    VariationAttributeType.Color
  );

  const sizeAttribute = product?.getSelectedAttributeByType(
    VariationAttributeType.Size
  );

  const size = product?.groupSize.size;

  const genderValue = productVM?.groupDisplayName;
  const genderAndSizeDisplayText = !isNullOrEmpty(genderValue)
    ? msgf(cart_cartItem_genderAndSize, {
        size: size ?? '?',
        gender: genderValue
      })
    : size;

  return (
    <div className={S.details}>
      {!isNullOrEmpty(sizeAttribute?.name) ? (
        <span>
          {variant !== 'simple' ? genderAndSizeDisplayText : genderValue}
        </span>
      ) : (
        <SkeletonLoader className={S.sizeLoader} />
      )}

      {!isNullish(color) && !isNullOrEmpty(color.name) ? (
        `${msg(cart_cartItem_color)}
      : ${color.name}`
      ) : (
        <SkeletonLoader className={S.colorLoader} />
      )}

      {variant === 'simple' && (
        <span>{msgf(cart_cartItem_sizeDisplay, { size: size ?? '' })}</span>
      )}

      {showActions && !isNullish(onClickEdit) && !isNullish(product) && (
        <Button variant="text" onClick={() => onClickEdit(product)}>
          {msg(cart_cartItem_editDetails)}
        </Button>
      )}
    </div>
  );
});

export interface IGiftCardDetailProps {
  /** The label to display. */
  label: string;
  /** The value to display. */
  value: string;
}
/** Displays the details of a gift card entry. */
export const GiftCardDetail: FC<IGiftCardDetailProps> = ({ label, value }) => (
  <div>
    <strong>{label}:</strong>
    <span className={S.detail}>{value}</span>
  </div>
);

export interface IGiftCardLineItemDetailsProps {
  /**
   * The gift card line item. This could receive an {@link IOrderLine}
   * when used at the order confirmation page.
   */
  line: IGiftCardLineItem | IOrderLine;
}

export const GiftCardLineItemDetails = observer(
  function GiftCardLineItemDetails({ line }: IGiftCardLineItemDetailsProps) {
    const [msg] = useLocaleMessage();
    return (
      <div className={S.details}>
        <GiftCardDetail
          label={msg(giftcard_cart_to)}
          value={`${line.recipientName}, ${isNullOrEmpty(line.recipientEmail) ? '' : maskEmail(line.recipientEmail)}`}
        />
        <GiftCardDetail
          label={msg(giftcard_cart_from)}
          value={line.senderName ?? ''}
        />
        <GiftCardDetail
          label={msg(giftcard_cart_total)}
          value={
            'amount' in line.unitPrice
              ? MoneyModel.from(line.unitPrice).toString()
              : line.unitPrice.price.toString()
          }
        />
      </div>
    );
  }
);
